import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { authService, User, AuthState } from '../services/authService';

interface AuthStore extends AuthState {
  // Auth initialization flag - true once auth state has been hydrated from storage
  isAuthInitialized: boolean;

  // Actions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signInWithOAuth: (provider: 'google' | 'microsoft') => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  updateSyncPreferences: (preferences: Partial<User['preferences']>) => Promise<void>;
  refreshAuthState: () => void;

  // 2FA state
  pendingTwoFactor: { tempToken: string; email: string } | null;
  verifyTwoFactorLogin: (code: string, isBackupCode?: boolean) => Promise<void>;
  clearPendingTwoFactor: () => void;

  // Sync actions
  syncToCloud: () => Promise<void>;
  enableAutoSync: (interval: 'realtime' | 'hourly' | 'daily') => Promise<void>;
  disableAutoSync: () => Promise<void>;

  // UI state
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
  authModalMode: 'signin' | 'signup' | 'forgot-password';
  setAuthModalMode: (mode: 'signin' | 'signup' | 'forgot-password') => void;
}

// Synchronously hydrate auth state from storage on module load
// This prevents the race condition where ProtectedRoute redirects before auth is initialized
const getInitialAuthState = () => {
  try {
    const authState = authService.getAuthState();
    return {
      ...authState,
      isAuthInitialized: true,
    };
  } catch {
    // If hydration fails, return default unauthenticated state
    return {
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      sessionExpiresAt: null,
      isAuthInitialized: true,
    };
  }
};

const initialAuthState = getInitialAuthState();

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      // Initial state - synchronously hydrated from storage
      ...initialAuthState,
      isLoading: false,
      showAuthModal: false,
      authModalMode: 'signin',
      pendingTwoFactor: null,

      // Sign in
      signIn: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const result = await authService.signIn(email, password);
          if (result && 'requires2FA' in result) {
            set({
              isLoading: false,
              pendingTwoFactor: { tempToken: result.tempToken, email: result.email },
            });
            return;
          }
          const authState = authService.getAuthState();
          set({
            ...authState,
            isLoading: false,
            showAuthModal: false
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Sign up
      signUp: async (email: string, password: string, name: string) => {
        set({ isLoading: true });
        try {
          await authService.signUp(email, password, name);
          const authState = authService.getAuthState();
          set({
            ...authState,
            isLoading: false,
            showAuthModal: false
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // OAuth sign in
      signInWithOAuth: async (provider: 'google' | 'microsoft') => {
        set({ isLoading: true });
        try {
          await authService.signInWithOAuth(provider);
          const authState = authService.getAuthState();
          set({
            ...authState,
            isLoading: false,
            showAuthModal: false
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Verify 2FA during login
      verifyTwoFactorLogin: async (code: string, isBackupCode = false) => {
        const pending = get().pendingTwoFactor;
        if (!pending) throw new Error('No pending 2FA challenge');

        set({ isLoading: true });
        try {
          await authService.verifyTwoFactorChallenge(pending.tempToken, code, isBackupCode);
          const authState = authService.getAuthState();
          set({
            ...authState,
            isLoading: false,
            showAuthModal: false,
            pendingTwoFactor: null,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Clear pending 2FA
      clearPendingTwoFactor: () => {
        set({ pendingTwoFactor: null });
      },

      // Sign out
      signOut: async () => {
        set({ isLoading: true });
        try {
          await authService.signOut();
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            sessionExpiresAt: null
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Update profile
      updateProfile: async (updates: Partial<User>) => {
        try {
          const updatedUser = await authService.updateProfile(updates);
          set({ user: updatedUser });
        } catch (error) {
          throw error;
        }
      },

      // Update sync preferences
      updateSyncPreferences: async (preferences: Partial<User['preferences']>) => {
        try {
          await authService.updateSyncPreferences(preferences);
          const authState = authService.getAuthState();
          set({ user: authState.user });
        } catch (error) {
          throw error;
        }
      },

      // Refresh auth state from service
      refreshAuthState: () => {
        const authState = authService.getAuthState();
        set({
          ...authState,
          isAuthInitialized: true,
        });
      },

      // Sync to cloud
      syncToCloud: async () => {
        await authService.syncToCloud();
      },

      // Enable auto sync
      enableAutoSync: async (interval: 'realtime' | 'hourly' | 'daily') => {
        await authService.updateSyncPreferences({
          enableCloudSync: true,
          syncInterval: interval
        });
        get().refreshAuthState();
      },

      // Disable auto sync
      disableAutoSync: async () => {
        await authService.updateSyncPreferences({
          enableCloudSync: false
        });
        get().refreshAuthState();
      },

      // UI state management
      setShowAuthModal: (show: boolean) => {
        set({ showAuthModal: show });
      },

      setAuthModalMode: (mode: 'signin' | 'signup' | 'forgot-password') => {
        set({ authModalMode: mode });
      }
    }),
    {
      name: 'auth-store'
    }
  )
);